import {
  CONSTRUCTED_DECK_SLOT_LIMIT,
  content,
  validateConstructedDeck,
} from '@cards/cards';
import type { CardDefId } from '@cards/engine';
import { parseDeckUuid } from '../decks/api.js';

export const FAST_ONBOARDING_VERSION = 1;

export interface FastOnboardingProgress {
  readonly version: 1;
  readonly presetDeckId: string | null;
  readonly presetId: string | null;
  readonly presetRevision: number | null;
  readonly gameTourCompletedAt: string | null;
  readonly onboardingSoloStartedAt: string | null;
  readonly lobbyTourCompletedAt: string | null;
}

export interface OnboardingPresetDto {
  readonly id: string;
  readonly revision: number;
  readonly name: string;
  readonly description: string;
  readonly strength: string;
  readonly weakness: string;
  readonly cardIds: readonly CardDefId[];
}

export interface OnboardingProjection {
  readonly progress: FastOnboardingProgress;
  readonly presets: readonly OnboardingPresetDto[];
}

export interface OnboardingDeckRef {
  readonly id: string;
  readonly revision: number;
}

export interface OnboardingPresetClaimResult {
  readonly progress: FastOnboardingProgress;
  readonly deck: OnboardingDeckRef;
  readonly replayed: boolean;
}

export type OnboardingMilestone =
  | 'game-tour-completed'
  | 'solo-started'
  | 'lobby-tour-completed';

export interface OnboardingApi {
  get(signal?: AbortSignal): Promise<OnboardingProjection>;
  claimPreset(
    presetId: string,
    presetRevision: number,
    operationId: string,
    csrfToken: string,
    signal?: AbortSignal,
  ): Promise<OnboardingPresetClaimResult>;
  recoverPreset(
    csrfToken: string,
    signal?: AbortSignal,
  ): Promise<OnboardingPresetClaimResult>;
  completeMilestone(
    milestone: OnboardingMilestone,
    csrfToken: string,
    signal?: AbortSignal,
  ): Promise<FastOnboardingProgress>;
}

type JsonObject = Record<string, unknown>;

function strictObject(value: unknown, keys: readonly string[], label: string): JsonObject {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new Error(`${label}: expected object`);
  }
  const object = value as JsonObject;
  const actual = Object.keys(object);
  if (actual.length !== keys.length || actual.some((key) => !keys.includes(key))) {
    throw new Error(`${label}: unknown or missing fields`);
  }
  return object;
}

function boundedString(value: unknown, label: string, maximum: number): string {
  if (typeof value !== 'string'
    || value.length === 0
    || value.length > maximum
    || value !== value.normalize('NFKC').trim()
    || /\p{C}/u.test(value)) {
    throw new Error(`${label}: expected normalized bounded string`);
  }
  return value;
}

function positiveInteger(value: unknown, label: string): number {
  if (!Number.isSafeInteger(value) || (value as number) <= 0) {
    throw new Error(`${label}: expected positive safe integer`);
  }
  return value as number;
}

function nullableTimestamp(value: unknown, label: string): string | null {
  if (value === null) return null;
  if (typeof value !== 'string'
    || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/u.test(value)
    || !Number.isFinite(Date.parse(value))
    || new Date(value).toISOString() !== value) {
    throw new Error(`${label}: expected UTC timestamp or null`);
  }
  return value;
}

export function parseFastOnboardingProgress(value: unknown): FastOnboardingProgress {
  const object = strictObject(value, [
    'version',
    'presetDeckId',
    'presetId',
    'presetRevision',
    'gameTourCompletedAt',
    'onboardingSoloStartedAt',
    'lobbyTourCompletedAt',
  ], 'onboarding progress');
  if (object.version !== FAST_ONBOARDING_VERSION) {
    throw new Error('onboarding progress: unsupported version');
  }
  const presetId = object.presetId === null
    ? null
    : exactPresetId(boundedString(object.presetId, 'presetId', 80));
  const presetRevision = object.presetRevision === null
    ? null
    : positiveInteger(object.presetRevision, 'presetRevision');
  const claimed = object.presetDeckId !== null;
  if (claimed !== (presetId !== null) || claimed !== (presetRevision !== null)) {
    throw new Error('onboarding progress: preset claim fields must be all null or all present');
  }
  return Object.freeze({
    version: FAST_ONBOARDING_VERSION,
    presetDeckId: object.presetDeckId === null
      ? null
      : parseDeckUuid(object.presetDeckId, 'presetDeckId'),
    presetId,
    presetRevision,
    gameTourCompletedAt: nullableTimestamp(object.gameTourCompletedAt, 'gameTourCompletedAt'),
    onboardingSoloStartedAt: nullableTimestamp(
      object.onboardingSoloStartedAt,
      'onboardingSoloStartedAt',
    ),
    lobbyTourCompletedAt: nullableTimestamp(
      object.lobbyTourCompletedAt,
      'lobbyTourCompletedAt',
    ),
  });
}

function parsePreset(value: unknown): OnboardingPresetDto {
  const object = strictObject(value, [
    'id', 'revision', 'name', 'description', 'strength', 'weakness', 'cardIds',
  ], 'onboarding preset');
  const id = boundedString(object.id, 'preset.id', 80);
  if (!/^[a-z][a-z0-9_]*$/u.test(id)) throw new Error('preset.id: invalid stable id');
  if (!Array.isArray(object.cardIds)
    || object.cardIds.length !== CONSTRUCTED_DECK_SLOT_LIMIT) {
    throw new Error('preset.cardIds: expected exact constructed deck');
  }
  const cardIds = Object.freeze(object.cardIds.map((cardId, index) =>
    boundedString(cardId, `preset.cardIds[${index}]`, 256) as CardDefId));
  const validation = validateConstructedDeck({ cardIds, passiveId: null }, content);
  if (!validation.ready) throw new Error('preset.cardIds: deck is not ready');
  return Object.freeze({
    id,
    revision: positiveInteger(object.revision, 'preset.revision'),
    name: boundedString(object.name, 'preset.name', 80),
    description: boundedString(object.description, 'preset.description', 360),
    strength: boundedString(object.strength, 'preset.strength', 240),
    weakness: boundedString(object.weakness, 'preset.weakness', 240),
    cardIds,
  });
}

export function parseOnboardingProjection(value: unknown): OnboardingProjection {
  const object = strictObject(value, ['progress', 'presets'], 'onboarding projection');
  if (!Array.isArray(object.presets) || object.presets.length !== 1) {
    throw new Error('onboarding presets: expected one starter');
  }
  const presets = Object.freeze(object.presets.map(parsePreset));
  if (new Set(presets.map((preset) => preset.id)).size !== presets.length) {
    throw new Error('onboarding presets: duplicate id');
  }
  return Object.freeze({
    progress: parseFastOnboardingProgress(object.progress),
    presets,
  });
}

function parseDeckRef(value: unknown): OnboardingDeckRef {
  const object = strictObject(value, ['id', 'revision'], 'onboarding deck ref');
  return Object.freeze({
    id: parseDeckUuid(object.id),
    revision: positiveInteger(object.revision, 'deck.revision'),
  });
}

export function parseOnboardingPresetClaimResult(value: unknown): OnboardingPresetClaimResult {
  const object = strictObject(value, ['progress', 'deck', 'replayed'], 'preset claim result');
  if (typeof object.replayed !== 'boolean') throw new Error('replayed: expected boolean');
  const progress = parseFastOnboardingProgress(object.progress);
  const deck = parseDeckRef(object.deck);
  if (progress.presetDeckId !== deck.id) {
    throw new Error('preset claim result: progress/deck mismatch');
  }
  return Object.freeze({ progress, deck, replayed: object.replayed });
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/u;

function exactOperationId(value: string): string {
  if (!UUID_PATTERN.test(value)) throw new Error('operationId: expected lowercase UUID v4');
  return value;
}

function exactPresetId(value: string): string {
  if (!/^[a-z][a-z0-9_]*$/u.test(value)) throw new Error('presetId: invalid stable id');
  return value;
}

export class OnboardingApiError extends Error {
  override readonly name = 'OnboardingApiError';

  constructor(
    readonly status: number,
    readonly code?: string,
  ) {
    super(code ?? `HTTP_${status}`);
  }
}

async function json(response: Response): Promise<unknown> {
  const mediaType = response.headers.get('content-type')?.split(';', 1)[0]?.trim().toLowerCase();
  if (mediaType !== 'application/json') throw new OnboardingApiError(response.status, 'INVALID_RESPONSE');
  try {
    return await response.json();
  } catch {
    throw new OnboardingApiError(response.status, 'INVALID_RESPONSE');
  }
}

async function responseValue(response: Response, expected: readonly number[]): Promise<unknown> {
  const value = await json(response);
  if (expected.includes(response.status)) return value;
  const code = typeof value === 'object'
    && value !== null
    && !Array.isArray(value)
    && typeof (value as JsonObject).error === 'string'
    ? (value as JsonObject).error as string
    : undefined;
  throw new OnboardingApiError(response.status, code);
}

function readInit(signal?: AbortSignal): RequestInit {
  return {
    method: 'GET',
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
    cache: 'no-store',
    redirect: 'error',
    ...(signal === undefined ? {} : { signal }),
  };
}

function mutationInit(
  method: 'POST' | 'PUT',
  csrfToken: string,
  body?: unknown,
  signal?: AbortSignal,
): RequestInit {
  return {
    method,
    headers: {
      Accept: 'application/json',
      'X-CSRF-Token': csrfToken,
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
    },
    credentials: 'same-origin',
    mode: 'cors',
    cache: 'no-store',
    redirect: 'error',
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    ...(signal === undefined ? {} : { signal }),
  };
}

export function createBrowserOnboardingApi(
  browserFetch: typeof fetch = globalThis.fetch.bind(globalThis),
): OnboardingApi {
  return {
    async get(signal) {
      const response = await browserFetch('/api/onboarding', readInit(signal));
      try {
        return parseOnboardingProjection(await responseValue(response, [200]));
      } catch (error) {
        if (error instanceof OnboardingApiError) throw error;
        throw new OnboardingApiError(response.status, 'INVALID_RESPONSE');
      }
    },
    async claimPreset(presetId, presetRevision, operationId, csrfToken, signal) {
      const request = {
        presetId: exactPresetId(presetId),
        presetRevision: positiveInteger(presetRevision, 'presetRevision'),
        operationId: exactOperationId(operationId),
      };
      const response = await browserFetch('/api/onboarding/preset-claim', mutationInit(
        'POST',
        csrfToken,
        request,
        signal,
      ));
      try {
        const result = parseOnboardingPresetClaimResult(
          await responseValue(response, [200, 201]),
        );
        if ((response.status === 200) !== result.replayed) {
          throw new Error('claim replay/status mismatch');
        }
        return result;
      } catch (error) {
        if (error instanceof OnboardingApiError) throw error;
        throw new OnboardingApiError(response.status, 'INVALID_RESPONSE');
      }
    },
    async recoverPreset(csrfToken, signal) {
      const response = await browserFetch(
        '/api/onboarding/preset-recovery',
        mutationInit('POST', csrfToken, undefined, signal),
      );
      try {
        return parseOnboardingPresetClaimResult(
          await responseValue(response, [200]),
        );
      } catch (error) {
        if (error instanceof OnboardingApiError) throw error;
        throw new OnboardingApiError(response.status, 'INVALID_RESPONSE');
      }
    },
    async completeMilestone(milestone, csrfToken, signal) {
      const response = await browserFetch(
        `/api/onboarding/milestones/${milestone}`,
        mutationInit('PUT', csrfToken, undefined, signal),
      );
      try {
        const object = strictObject(
          await responseValue(response, [200]),
          ['progress'],
          'milestone result',
        );
        return parseFastOnboardingProgress(object.progress);
      } catch (error) {
        if (error instanceof OnboardingApiError) throw error;
        throw new OnboardingApiError(response.status, 'INVALID_RESPONSE');
      }
    },
  };
}
